import { getVendorSubdomain } from "../Request";
import { ApplicationCommunicator, type ApplicationMessageHandlerData } from "./ApplicationCommunicator";

export class ApplicationFrameCommunicator extends ApplicationCommunicator {
    protected frameURL!: URL | string;
    protected frame: HTMLIFrameElement | null;
    protected resolvedUrl!: URL | string;

    constructor(
        url: string,
        handleMessage: (data: ApplicationMessageHandlerData) => void,
        getName: () => string
    ) {
        super(url, handleMessage, getName);

        this.setURL(url);
        this.frame = null;
    }

    /**
     * Determine the URL to use for the frame, ensuring we're passing the vendor in
     */
    protected setURL(url: string): void {
        const subdomain = getVendorSubdomain();

        try {
            this.resolvedUrl = new URL(url);

            this.frameURL = new URL(url);
            this.frameURL.searchParams.set("vendor", subdomain);
        } catch {
            this.resolvedUrl = url;

            let append;

            if(this.resolvedUrl.includes("?")) {
                append = "&";
            } else {
                append = "?";
            }

            this.frameURL = `${this.resolvedUrl}${append}vendor=${subdomain}`;
        }
    }

    protected handleFrameMessage = (e: MessageEvent): void => {
        if(!this.frame) {
            return;
        }

        /* this doesn't work because of the sandbox, so we need to emulate it
        if(this.url instanceof URL && e.origin !== this.url.origin) {
            return;
        }
        */

        if(e.source !== this.frame.contentWindow) {
            // Unfortunately, HappyDOM doesn't correctly return the source
            if(e.source === null || process.env.NODE_ENV !== "test" || !("happyDOM" in e.source)) {
                return;
            }
        }

        if(typeof e.data !== "object" || e.data === null) {
            return;
        }

        if(typeof e.data.origins === "undefined") {
            return;
        } else if(this.resolvedUrl instanceof URL && e.data.origins.from !== this.resolvedUrl.origin) {
            return;
        } else if(e.data.origins.to !== window.location.origin) {
            return;
        }

        if(typeof e.data.key === "undefined") {
            return;
        }

        if(typeof e.data.type === "undefined") {
            return;
        }

        this.handleMessage(e.data);
    };

    /**
     * @inheritDoc
     */
    public async create(): Promise<void> {
        // Ensure the frame URL is valid
        const frameURL = this.frameURL.toString();

        if(!frameURL.startsWith("https://") && !frameURL.startsWith("http://localhost/")) {
            console.error(`Not loading application ${this.getName()} as Embedded URL is not secure: ${frameURL}`);

            return;
        }

        // Create the frame
        const frame = document.createElement("iframe");

        frame.sandbox.add("allow-scripts", "allow-same-origin");
        frame.src = frameURL;
        frame.classList.add("embedded-application");

        // Store the reference
        this.frame = frame;

        // Append and listen to the frame
        window.addEventListener("message", this.handleFrameMessage);
        document.body.appendChild(frame);
    }

    public async destroy(): Promise<void> {
        if(this.frame === null) {
            return;
        }

        window.removeEventListener("message", this.handleFrameMessage);
        document.body.removeChild(this.frame);

        this.frame = null;
    }

    /**
     * @inheritDoc
     */
    public async update(url: string): Promise<void> {
        if(this.url !== url) {
            console.info(`Changed ${this.getName()} URL`);

            this.setURL(url);
            await this.destroy();
            await this.create();
        }
    }

    /**
     * @inheritDoc
     */
    public sendMessage(type: string, data: unknown, id?: string): boolean {
        if(!this.frame || !this.frame.contentWindow) {
            return false;
        }

        const target = this.resolvedUrl instanceof URL ? this.resolvedUrl.origin : this.resolvedUrl;

        this.frame.contentWindow.postMessage({
            from   : "ShopfrontApplicationAgent",
            origins: {
                to  : target,
                from: window.location.origin,
            },
            type,
            data,
            id,
        }, "*");

        return true;
    }

    /**
     * @inheritDoc
     */
    public isReady(): boolean {
        return this.frame !== null && this.frame.contentWindow !== null;
    }

    /**
     * @inheritDoc
     */
    public getRawUrl(): URL | string {
        return this.resolvedUrl;
    }

    /**
     * @inheritDoc
     */
    public getLoadedUrl(): URL | string {
        return this.frameURL;
    }

    /**
     * @inheritDoc
     */
    public isEventFromSource(event: MessageEvent): boolean {
        return event.source === this.frame?.contentWindow;
    }
}
